import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import {GetAppOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import Message from '../../../shared/message/Message.js';
import useFetch from '../../../../hooks/useFetch.js';
import {URL_AVALIACAO_DOCUMENTOS_ARQUIVO} from '../../../../constants/index.js';
import {mensagemDoErro} from '../../../../utils/erro.js';
import {salvarArquivoResposta} from '../../../../utils/download.js';

const MENSAGEM_ERRO = 'Não foi possível carregar o documento.';
const MIMETYPES_IMAGEM = ['image/jpeg', 'image/jpg', 'image/png'];
const MIMETYPE_PDF = 'application/pdf';

const TIPO_PDF = 'pdf';
const TIPO_IMAGEM = 'imagem';
const TIPO_OUTRO = 'outro';

const useStyles = makeStyles(theme => ({
    // Ocupa toda a altura que o painel em volta der (o painel é uma coluna flex).
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 200
    },
    carregando: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(6, 0)
    },
    pdf: {
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: 240,
        border: `1px solid ${theme.palette.secondary.main}`,
        borderRadius: theme.shape.borderRadius
    },
    imagemContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflow: 'auto',
        minHeight: 240,
        border: `1px solid ${theme.palette.secondary.main}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.secondary.light
    },
    imagem: {
        maxWidth: '100%',
        height: 'auto',
        objectFit: 'contain'
    },
    outroTipo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(2)
    }
}));

/* PDF e imagens (jpeg/jpg/png) abrem na tela; qualquer outro tipo só baixa. */
export const tipoVisualizacao = mimetype => {
    const tipo = (mimetype || '').toLowerCase();

    if (tipo.includes(MIMETYPE_PDF)) {
        return TIPO_PDF;
    }

    return MIMETYPES_IMAGEM.includes(tipo) ? TIPO_IMAGEM : TIPO_OUTRO;
};

/* Erro de um GET com responseType 'blob': o corpo do erro também chega como Blob. */
const lerCorpoDoErro = erro => (erro && typeof erro.text === 'function' ? erro.text() : Promise.resolve(erro));

const interpretarErro = texto => {
    try {
        return JSON.parse(texto);
    } catch (e) {
        return texto;
    }
};

/*
 * Mostra o arquivo de um anexo. O endpoint exige o header `credencial`, então o
 * <iframe>/<img> não podem apontar direto para a URL: o arquivo é baixado como
 * Blob pelo useFetch (responseType 'blob', que o features/async repassa ao axios)
 * e exibido por um objectURL, revogado ao trocar de documento ou desmontar.
 */
export default function VisualizadorDocumento({idAnexo, mimetype, nomeArquivo}) {
    const classes = useStyles();
    const tipo = tipoVisualizacao(mimetype);
    const [buscarArquivo, carregando] = useFetch({returnInnerLoadingState: true, responseType: 'blob'});
    const [urlObjeto, setUrlObjeto] = useState(null);
    const [erro, setErro] = useState('');

    const tratarFalha = (error, ativo) => {
        lerCorpoDoErro(error).then(corpo => {
            if (ativo()) {
                setErro(mensagemDoErro(typeof corpo === 'string' ? interpretarErro(corpo) : corpo, MENSAGEM_ERRO));
            }
        });
    };

    useEffect(() => {
        setUrlObjeto(null);
        setErro('');

        if (!idAnexo || tipo === TIPO_OUTRO) {
            return undefined;
        }

        let ativo = true;
        let url = null;

        buscarArquivo(
            URL_AVALIACAO_DOCUMENTOS_ARQUIVO(idAnexo),
            {},
            blob => {
                if (ativo) {
                    url = window.URL.createObjectURL(new Blob([blob], {type: mimetype}));
                    setUrlObjeto(url);
                }
            },
            error => tratarFalha(error, () => ativo));

        return () => {
            ativo = false;

            if (url) {
                window.URL.revokeObjectURL(url);
            }
        };
    }, [idAnexo, mimetype]);

    const baixar = () => {
        buscarArquivo(
            URL_AVALIACAO_DOCUMENTOS_ARQUIVO(idAnexo),
            {},
            blob => salvarArquivoResposta({data: blob, headers: {}}, nomeArquivo || 'documento'),
            error => tratarFalha(error, () => true));
    };

    if (tipo === TIPO_OUTRO) {
        return (
            <div className={classes.outroTipo}>
                <Typography variant='body2'>
                    Este tipo de arquivo não pode ser visualizado na tela. Baixe o arquivo para abri-lo.
                </Typography>
                <ContainedButton startIcon={<GetAppOutlined/>} onClick={baixar} disabled={carregando}>
                    Baixar arquivo
                </ContainedButton>
                {!!erro && <Message type='error'>{erro}</Message>}
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {carregando && (
                <div className={classes.carregando}>
                    <CircularProgress/>
                </div>
            )}
            {!carregando && !!erro && <Message type='error'>{erro}</Message>}
            {!carregando && !erro && !!urlObjeto && tipo === TIPO_PDF && (
                <iframe
                    title={nomeArquivo || 'Documento'}
                    src={urlObjeto}
                    className={classes.pdf}/>
            )}
            {!carregando && !erro && !!urlObjeto && tipo === TIPO_IMAGEM && (
                <div className={classes.imagemContainer}>
                    <img src={urlObjeto} alt={nomeArquivo || 'Documento'} className={classes.imagem}/>
                </div>
            )}
        </div>
    );
}

VisualizadorDocumento.propTypes = {
    idAnexo: PropTypes.number,
    mimetype: PropTypes.string,
    nomeArquivo: PropTypes.string
};
